import { Vector2 } from "../../../common/vector2.js";
import { ImgID } from "../../../image_loader/img_id.js";
import { BasicEventsController } from "../../../touch_events_controller/basic_events_controller.js";
import { Easing } from "../../../transforms/transform_base.js";
import { TransformStraightMove } from "../../../transforms/transform_straight_move.js";
import { ColorScheme } from "../../common_ui_elements/color_scheme.js";
import { TextStyleScheme } from "../../common_ui_elements/text_style_scheme.js";
import { UIElement } from "../../common_ui_elements/ui_element.js";
import { UIElementHolder } from "../../common_ui_elements/ui_element_holder.js";
import { UIImage } from "../../common_ui_elements/ui_image.js";
import { MyTextAlign, MyTextVerticalAlign, UITextElement, UITextGradient } from "../../common_ui_elements/ui_text_element.js";
import { ScreenManager, Screens } from "../../../screen_manager/screen_manager.js";
import { formatChainOfferRemaining, getChainOfferRemainingMs } from "./chain_offer_timer.js";
import { Game } from "../../../game.js";
import { SafeAreas } from "../../safe_areas.js";
import { ChainOfferItem, ChainOfferItemType } from "./chain_offer_item.js";
import type { ChainOfferRewardFlySource } from "./chain_offer_item.js";
import { RewardFlyToHud } from "../../reward_fly/reward_fly_to_hud.js";
import type { RewardFlySource } from "../../reward_fly/reward_fly_to_hud.js";
import { OfferService } from "../../../offers/offer_service.js";
import { IapPlugin } from "../../../plugins/iap_plugin.js";
import { OfferStore } from "../../../api/dto/offers.dto.js";
import type { ChainOfferStepDto, OfferDto, OfferProductDto, OfferRewardType } from "../../../api/dto/offers.dto.js";

interface TitleLayerConfig
{
  outline_width_design: number;
  outline_offset_y_design: number;
  outline_color?: string;
  outline_gradient_color?: UITextGradient;
  shadow_blur_design?: number;
  shadow_color?: string;
  shadow_offset_y_design?: number;
}

export class ChainOfferScreen extends UIElement
{
  private static readonly SNAKE_SHIFT_MOVES: readonly ( readonly [ number, number ] )[] = [
    [ 1, 0 ], [ 3, 1 ], [ 2, 3 ], [ 4, 2 ], [ 5, 4 ],
  ];

  private static readonly SNAKE_SHIFT_MS = 450;

  /**
   * How long before the shift ends the incoming card starts popping in, so the
   * last slot is never left visibly empty waiting for the chain to settle.
   */
  private static readonly REVEAL_LEAD_MS = 220;

  /**
   * Slot each chain step occupies, in the order the snake walks the path:
   * the chain climbs down the S-shape, so a step's queue position is not its
   * slot index.
   */
  private static readonly SNAKE_SLOT_ORDER: readonly number[] = [ 0, 1, 3, 2, 4, 5 ];

  private static readonly OFFER_APPEAR_STAGGER_MS = 72;

  private events_controller: BasicEventsController | null = null;
  private background_gradient: CanvasGradient | null = null;
  private timer_bg: UIImage | null = null;
  private timer_text: UITextElement | null = null;
  private timer_refreshed_at = 0;
  private last_timer_text = "";
  private timer_slot_index = 0;

  private content: UIElementHolder | null = null;
  private content_x = 0;
  private offer_items: ( ChainOfferItem | null )[] = [];
  private offer_positions: { x: number; y: number }[] = [];
  private offer_w = 0;
  private offer_h = 0;
  /** Index into the chain steps of the next card to slide onto the board. */
  private next_reveal_index = 0;
  private shown_tracked = false;
  private exiting = false;
  private offers_key = "";

  private readonly handleEntitlementsChanged = (): void => { this.reconcileWithServer(); };

  constructor( ctx: CanvasRenderingContext2D, x: number, y: number, width: number, height: number )
  {
    super( ctx, x, y, width, height, false, false );

    this.initializeEventsController( this.ctx.canvas );
    window.addEventListener( 'offersEntitlementsChanged', this.handleEntitlementsChanged );

    this.createTopCloud();
    this.createCloudGroup();
    this.createConfetti();
    this.createBalloonIcon();
    this.createContent();
  }

  private createTopCloud(): void
  {
    const top_cloud_w_design = 1408;
    const top_cloud_h_design = 939;
    const top_cloud_y_design = 469;
    const top_cloud_x_min_design = 524;
    const top_cloud_x_max_design = 346;

    const top_cloud_w = LayoutScaler.widthFromDesign( top_cloud_w_design );
    const top_cloud_h = LayoutScaler.heightFromDesign( top_cloud_h_design );
    const top_cloud_x = LayoutScaler.xFromLayouts( top_cloud_x_min_design, top_cloud_x_max_design );
    const top_cloud_y = -LayoutScaler.yFromDesign( top_cloud_y_design );

    const top_cloud = new UIImage( this.ctx, top_cloud_x, top_cloud_y, top_cloud_w, top_cloud_h, ImgID.UI_ChainOfferCloud );
    this.addChild( top_cloud );
  }

  private createCloudGroup(): void
  {
    const cloud_a_x_design = -485;
    const cloud_a_w_design = 2136;
    const cloud_a_h_design = 1424;
    const cloud_a_y_min_design = 813;
    const cloud_a_y_max_design = 1170;

    const cloud_a_w = LayoutScaler.widthFromDesign( cloud_a_w_design );
    const cloud_a_h = LayoutScaler.heightFromDesign( cloud_a_h_design );
    const cloud_a_x = LayoutScaler.xFromDesign( cloud_a_x_design );
    const cloud_a_y = LayoutScaler.yFromLayouts( cloud_a_y_min_design, cloud_a_y_max_design );

    const cloud_a = new UIImage( this.ctx, cloud_a_x, cloud_a_y, cloud_a_w, cloud_a_h, ImgID.UI_ChainOfferCloud );
    this.addChild( cloud_a );

    const cloud_c_x_design = 492;
    const cloud_c_w_design = 1408;
    const cloud_c_h_design = 939;
    const cloud_c_y_min_design = 1362;
    const cloud_c_y_max_design = 1758;

    const cloud_c_w = LayoutScaler.widthFromDesign( cloud_c_w_design );
    const cloud_c_h = LayoutScaler.heightFromDesign( cloud_c_h_design );
    const cloud_c_x = LayoutScaler.xFromDesign( cloud_c_x_design );
    const cloud_c_y = LayoutScaler.yFromLayouts( cloud_c_y_min_design, cloud_c_y_max_design );

    const cloud_c = new UIImage( this.ctx, cloud_c_x, cloud_c_y, cloud_c_w, cloud_c_h, ImgID.UI_ChainOfferCloud );
    this.addChild( cloud_c );

    const cloud_b_x_design = -677;
    const cloud_b_w_design = 1217;
    const cloud_b_h_design = 811;
    const cloud_b_y_offset_design = 64;

    const cloud_b_w = LayoutScaler.widthFromDesign( cloud_b_w_design );
    const cloud_b_h = LayoutScaler.heightFromDesign( cloud_b_h_design );
    const cloud_b_x = LayoutScaler.xFromDesign( cloud_b_x_design );
    const cloud_b_y = cloud_c_y + LayoutScaler.yFromDesign( cloud_b_y_offset_design );

    const cloud_b = new UIImage( this.ctx, cloud_b_x, cloud_b_y, cloud_b_w, cloud_b_h, ImgID.UI_ChainOfferCloud );
    this.addChild( cloud_b );
  }

  private createConfetti(): void
  {
    const confetti_w_design = 1275;
    const confetti_h_design = 1744;
    const confetti_y_min_design = 163;
    const confetti_y_max_design = 109;

    const base_confetti_w = LayoutScaler.widthFromDesign( confetti_w_design );
    const base_confetti_h = LayoutScaler.heightFromDesign( confetti_h_design );
    const base_confetti_top = -LayoutScaler.yFromLayouts( confetti_y_min_design, confetti_y_max_design );

    // On wide screens the design-width art leaves bare strips at the sides —
    // stretch it to cover the full width, keeping aspect ratio and pinning the
    // bottom edge so the extra height only spills further off the top.
    const confetti_w = Math.max( base_confetti_w, this.rect.getW() );
    const confetti_scale = confetti_w / base_confetti_w;
    const confetti_h = base_confetti_h * confetti_scale;
    const confetti_x = ( this.rect.getW() - confetti_w ) / 2;
    const confetti_y = base_confetti_top + base_confetti_h - confetti_h;

    const confetti = new UIImage( this.ctx, confetti_x, confetti_y, confetti_w, confetti_h, ImgID.UI_ChainOfferConfetti );
    this.addChild( confetti );
  }

  private createBalloonIcon(): void
  {
    const balloon_w_min_design = 648;
    const balloon_w_max_design = 1006;
    const balloon_h_min_design = 712;
    const balloon_h_max_design = 1105;
    const balloon_y_design = 109;

    const balloon_w = LayoutScaler.widthFromHeightLayouts( balloon_w_min_design, balloon_w_max_design );
    const balloon_h = LayoutScaler.heightFromLayouts( balloon_h_min_design, balloon_h_max_design );
    const balloon_x = ( this.rect.getW() - balloon_w ) / 2;
    const balloon_y = -LayoutScaler.yFromDesign( balloon_y_design ) - LayoutScaler.yFromDesign( 100 );

    const balloon = new UIImage( this.ctx, balloon_x, balloon_y, balloon_w, balloon_h, ImgID.UI_ChainOfferBalloonIcon );
    this.addChild( balloon );
  }

  private createContent(): void
  {
    const content_w_design = 1040;
    const content_h_design = 1683;
    const content_y_min_design = 220;
    const content_y_max_design = 623;

    const content_w = LayoutScaler.widthFromDesign( content_w_design );
    const content_h = LayoutScaler.heightFromDesign( content_h_design );
    const content_x = ( this.rect.getW() - content_w ) / 2;
    const content_y = LayoutScaler.yFromLayouts( content_y_min_design, content_y_max_design ) - LayoutScaler.yFromDesign( 100 );

    const content = new UIElementHolder( this.ctx, content_x, content_y, content_w, content_h );
    this.addChild( content );
    this.content = content;
    this.content_x = content_x;

    this.createContentTitle( content );
    this.createCloseButton( content_x, content_y );
    this.createContentTimer( content );
    this.createContentSnakeChain( content );
    this.createOffers( content );
  }

  private createContentSnakeChain( content: UIElementHolder ): void
  {
    const snake_chain_w_design = 692;
    const snake_chain_h_design = 1076;
    const snake_chain_x_design = 175;
    const snake_chain_y_design = 466;

    const snake_chain_w = LayoutScaler.widthFromDesign( snake_chain_w_design );
    const snake_chain_h = LayoutScaler.heightFromDesign( snake_chain_h_design );
    const snake_chain_x = LayoutScaler.xFromDesign( snake_chain_x_design );
    const snake_chain_y = LayoutScaler.yFromDesign( snake_chain_y_design );

    const snake_chain = new UIImage( this.ctx, snake_chain_x, snake_chain_y, snake_chain_w, snake_chain_h, ImgID.UI_ChainOfferSnakeChain );
    content.addChild( snake_chain );
  }

  private createOffers( content: UIElementHolder ): void
  {
    const offer_w_design = 496;
    const offer_h_design = 412;
    const top_y_design = 350;
    const side_y_design = 810;

    const offer_w = LayoutScaler.widthFromDesign( offer_w_design );
    const offer_h = LayoutScaler.heightFromDesign( offer_h_design );
    const left_x = 0;
    const right_x = content.getRect().getW() - offer_w;
    const top_y = LayoutScaler.yFromDesign( top_y_design );
    const side_y = LayoutScaler.yFromDesign( side_y_design );
    const bottom_y = content.getRect().getH() - offer_h;

    const offer_positions = [
      { x: left_x, y: top_y },
      { x: right_x, y: top_y },
      { x: left_x, y: side_y },
      { x: right_x, y: side_y },
      { x: left_x, y: bottom_y },
      { x: right_x, y: bottom_y },
    ];
    this.offer_w = offer_w;
    this.offer_h = offer_h;
    this.offer_positions = offer_positions;
    this.populateOffers();
    if ( !OfferService.getChainOffers().length )
      void OfferService.refresh().then( () => this.refreshTimer() );
  }

  private getPlatformProduct( step: ChainOfferStepDto ): OfferProductDto | null
  {
    const products = step.products;
    if ( !products ) return null;
    const store = IapPlugin.getStore();
    if ( store === OfferStore.GooglePlay ) return products.android ?? null;
    if ( store === OfferStore.AppStore ) return products.ios ?? null;
    return products.android ?? products.ios ?? null;
  }

  /**
   * A looping chain repeats its configured cards until it fills every visual
   * slot on the path, so a 4-step loop still occupies all 6 slots.
   */
  private getChainStepAt( index: number ): { offer: OfferDto; step: ChainOfferStepDto } | null
  {
    const offer = OfferService.getChainOffers()[0];
    const chain = offer?.chain;
    if ( !offer || !chain || !chain.steps.length ) return null;

    if ( chain.repeat ) return { offer, step: chain.steps[index % chain.steps.length] };
    return index < chain.steps.length ? { offer, step: chain.steps[index] } : null;
  }

  private createOfferItem( offer: OfferDto, step: ChainOfferStepDto, slot: number, locked: boolean ): ChainOfferItem | null
  {
    const content = this.content;
    if ( !content ) return null;

    const is_rewarded = step.rewarded === true;
    const product = step.free || is_rewarded ? null : this.getPlatformProduct( step );
    if ( !step.free && !is_rewarded && !product ) return null;

    const rewards = Object.entries( step.rewards )
      .filter( ( [ type, amount ] ) => ( type === 'no_ads' && amount === true ) || ( typeof amount === 'number' && amount > 0 ) )
      .map( ( [ type, amount ] ) => ( {
        type: type as OfferRewardType,
        ...( typeof amount === 'number' ? { amount } : {} ),
      } ) )
      // Permanent No Ads is the most important part of a bundle, so it must
      // never be hidden behind the card's compact reward layout.
      .sort( ( left, right ) => Number( right.type === 'no_ads' ) - Number( left.type === 'no_ads' ) );
    const position = this.offer_positions[slot];

    const item = new ChainOfferItem( this.ctx, position.x, position.y, this.offer_w, this.offer_h, {
      offer_id: offer.id,
      step_id: step.id,
      type: step.free ? ChainOfferItemType.Free : is_rewarded ? ChainOfferItemType.Video : ChainOfferItemType.Price,
      locked,
      ...( !step.free && !is_rewarded ? { price: '…' } : {} ),
      rewards,
    } );
    content.addChild( item );
    this.offer_items[slot] = item;
    item.setOnRewardsClaimed( ( sources, stats_before ) =>
    {
      if ( !this.exiting )
        RewardFlyToHud.play( this.ctx, this, sources.map( ChainOfferScreen.toRewardFlySource ), stats_before );
    } );

    // Only the current card may be interacted with. Free cards become
    // claimable after the preceding paid purchase.
    if ( !locked )
      this.armOfferExit( item );

    if ( product ) void this.loadPrice( item, offer.id, step.id, product );

    return item;
  }

  private static toRewardFlySource( source: ChainOfferRewardFlySource ): RewardFlySource
  {
    const is_heart = source.type === "hearts" || source.type === "infinite_hearts_minutes";
    return {
      target: source.type === "coins" ? "coin" : is_heart ? "heart" : "avatar",
      img_id: source.img_id,
      center: source.center,
      size: source.size,
      ...( source.amount !== undefined ? { amount: source.amount } : {} ),
      copies: source.type === "hearts" ? Math.max( 1, Math.min( 3, source.amount ?? 1 ) ) : 1,
    };
  }

  private static computeOffersKey(): string
  {
    return JSON.stringify( OfferService.getChainOffers()[0] ?? null );
  }

  public canReuseScreen(): boolean
  {
    return this.offers_key !== "" && this.offers_key === ChainOfferScreen.computeOffersKey();
  }

  public onScreenHidden(): void
  {
    this.events_controller?.disable();
  }

  public onScreenShown(): void
  {
    this.exiting = false;
    this.events_controller?.enable();
    this.refreshTimer();
  }

  private populateOffers(): void
  {
    const content = this.content;
    if ( !content ) return;
    this.offers_key = ChainOfferScreen.computeOffersKey();
    for ( const item of this.offer_items ) if ( item ) content.removeChild( item );
    this.offer_items = new Array( this.offer_positions.length ).fill( null );

    const offer = OfferService.getChainOffers()[0];
    const chain = offer?.chain;
    const active_step_id = chain?.next_step_id ?? chain?.next_paid_step_id;
    if ( !offer || !chain || !active_step_id ) return;

    const active_index = chain.steps.findIndex( step => step.id === active_step_id );
    if ( active_index < 0 ) return;

    if ( !this.shown_tracked )
    {
      this.shown_tracked = true;
      void OfferService.trackShown( offer.id, { placement: 'chain_offer' } );
    }

    ChainOfferScreen.SNAKE_SLOT_ORDER.forEach( ( slot, order ) =>
    {
      const next = this.getChainStepAt( active_index + order );
      if ( next ) this.createOfferItem( next.offer, next.step, slot, order !== 0 );
    } );

    this.next_reveal_index = active_index + this.offer_positions.length;

    this.playOffersAppearAnimation();
  }

  private reconcileWithServer(): void
  {
    if ( !this.content || !this.offer_positions.length || this.exiting )
      return;

    const offer = OfferService.getChainOffers()[0];
    const chain = offer?.chain;
    const server_active_id = chain?.next_step_id ?? chain?.next_paid_step_id;

    if ( !offer || !chain || !server_active_id )
    {
      if ( this.shown_tracked )
        this.exitToMenu( 'offer_gone' );
      return;
    }

    const server_index = chain.steps.findIndex( step => step.id === server_active_id );
    if ( server_index < 0 )
      return;

    const local_id = this.offer_items[0]?.getStepId();
    const local_index = local_id ? chain.steps.findIndex( step => step.id === local_id ) : -1;

    if ( local_index >= 0 )
    {
      const advanced_by = ( server_index - local_index + chain.steps.length ) % chain.steps.length;
      if ( advanced_by <= 1 )
        return;
    }

    this.populateOffers();
  }

  private exitToMenu( reason: 'user_close' | 'chain_complete' | 'offer_gone' ): void
  {
    if ( this.exiting )
      return;
    this.exiting = true;

    const offer_id = OfferService.getChainOffers()[0]?.id;
    if ( offer_id )
      void OfferService.trackClosed( offer_id, { placement: 'chain_offer', reason } );

    void ScreenManager.showScreen( Screens.MENU );
  }

  private playOffersAppearAnimation(): void
  {
    const start_base = performance.now();
    const reveal_order = [ ...ChainOfferScreen.SNAKE_SLOT_ORDER ].reverse(); // queue order 5 → 0

    reveal_order.forEach( ( slot, i ) =>
    {
      this.offer_items[ slot ]?.playAppearAnimation( start_base + i * ChainOfferScreen.OFFER_APPEAR_STAGGER_MS );
    } );
  }

  private playSnakeShift(): void
  {
    if ( !this.content )
      return;

    // The purchased card is still playing its own exit here; it detaches itself
    // when that ends, so it must not be pulled out of the tree by the shift.
    const now = performance.now();

    for ( const [ from_index, to_index ] of ChainOfferScreen.SNAKE_SHIFT_MOVES )
    {
      const item = this.offer_items[ from_index ];
      if ( !item )
      {
        this.offer_items[ to_index ] = null;
        continue;
      }

      this.content.removeChild( item );
      this.content.addChild( item );

      const dest_pos = this.offer_positions[ to_index ];
      const move = new TransformStraightMove( item, true, null, new Vector2( dest_pos.x, dest_pos.y ), now, ChainOfferScreen.SNAKE_SHIFT_MS, { easing: Easing.EaseInOutQuad } );

      if ( to_index === 0 )
        move.setOnFinished( () => this.activateOffer( item ) );

      item.addTransform( move );

      this.offer_items[ to_index ] = item;
    }

    this.offer_items[ 5 ] = null;
    this.revealNextOffer( now + Math.max( 0, ChainOfferScreen.SNAKE_SHIFT_MS - ChainOfferScreen.REVEAL_LEAD_MS ) );
  }

  /**
   * The purchased card keeps animating after the chain starts shifting, so it
   * detaches itself once its own exit ends instead of being pulled out by the shift.
   */
  private armOfferExit( item: ChainOfferItem ): void
  {
    item.setOnExitStarted( () => this.playSnakeShift() );
    item.setOnExitFinished( () =>
    {
      this.content?.removeChild( item );

      if ( this.offer_items.every( it => !it ) )
        this.exitToMenu( 'chain_complete' );
    } );
  }

  private activateOffer( item: ChainOfferItem ): void
  {
    item.unlock();
    this.armOfferExit( item );
  }

  private revealNextOffer( start_time: number ): void
  {
    if ( !this.content )
      return;

    const next = this.getChainStepAt( this.next_reveal_index );
    if ( !next )
      return;

    this.next_reveal_index++;

    const last_slot = ChainOfferScreen.SNAKE_SLOT_ORDER[ ChainOfferScreen.SNAKE_SLOT_ORDER.length - 1 ];
    const item = this.createOfferItem( next.offer, next.step, last_slot, true );
    item?.playAppearAnimation( start_time );
  }

  private async loadPrice( item: ChainOfferItem, offer_id: string, step_id: string, product: OfferProductDto ): Promise<void>
  {
    try
    {
      const details = await IapPlugin.getProductDetails( product.productId );
      item.setPrice( details?.formatted_price ?? 'Buy' );
      await OfferService.trackPriceLoaded( offer_id, { placement: 'chain_offer', chain_step_id: step_id, productid: product.productId } );
    }
    catch
    {
      item.setPrice( 'Buy' );
      await OfferService.trackPriceUnavailable( offer_id, { placement: 'chain_offer', chain_step_id: step_id, productid: product.productId } );
    }
  }

  /**
   * Not a child of `content` — it must stay clickable/laid out independent of
   * the content block. Its design offset is added on top of content's own
   * x/y so it lands on the exact same screen position as before.
   */
  private createCloseButton( content_x: number, content_y: number ): void
  {
    const close_icon_w_design = 110;
    const close_icon_h_design = 110;
    const close_icon_x_design = 897;
    const close_icon_y_design = 0;

    const close_icon_w = LayoutScaler.widthFromDesign( close_icon_w_design );
    const close_icon_h = LayoutScaler.heightFromDesign( close_icon_h_design );
    const close_icon_x = content_x + LayoutScaler.xFromLayouts( close_icon_x_design , close_icon_x_design + 200  );
    const close_icon_y = content_y + LayoutScaler.yFromDesign( close_icon_y_design );

    const close_icon = new UIImage( this.ctx, close_icon_x, close_icon_y, close_icon_w, close_icon_h, ImgID.UI_SettingsDialogCloseBtn, {}, true );
    this.addChild( close_icon );

    close_icon.setOnClick( () => this.exitToMenu( 'user_close' ) );
  }

  private createContentTimer( content: UIElementHolder ): void
  {
    this.timer_slot_index = content.getChildren().length;
    this.buildTimer();
  }

  private buildTimer(): void
  {
    const content = this.content;
    if ( !content || this.timer_bg ) return;

    const remaining_ms = getChainOfferRemainingMs();
    if ( remaining_ms === null ) return;

    const timer_w_design = 288;
    const timer_h_design = 78;
    const timer_y_design = 273;
    const timer_font_size_design = 45;
    const icon_area_w_design = 72;
    const text_gap_design = 14;

    const timer_w = LayoutScaler.widthFromDesign( timer_w_design );
    const timer_h = LayoutScaler.heightFromDesign( timer_h_design );
    const timer_x = ( content.getRect().getW() - timer_w ) / 2;
    const timer_y = LayoutScaler.yFromDesign( timer_y_design );

    const timer_bg = new UIImage( this.ctx, timer_x, timer_y, timer_w, timer_h, ImgID.UI_ChainOfferTimerSubstrate );
    content.addChildAtIndex( timer_bg, Math.min( this.timer_slot_index, content.getChildren().length ) );

    const text_gap = LayoutScaler.xFromDesign( text_gap_design );
    const text_x = LayoutScaler.widthFromDesign( icon_area_w_design );
    const text_w = timer_w - text_x - text_gap;

    this.last_timer_text = formatChainOfferRemaining( remaining_ms );
    const timer_text = new UITextElement( this.ctx, text_x, 0, text_w, timer_h, {
      text: this.last_timer_text,
      font_size: LayoutScaler.textSizeFromDesign( timer_font_size_design ),
      font_weight: 900,
      text_style: TextStyleScheme.geologica,
      text_align: MyTextAlign.Center,
      vertical_align: MyTextVerticalAlign.Center,
      center_on_cap_height: true,
      ...TextStyleScheme.timerShadow(),
      text_color: ColorScheme.white,
      scale_to_width: true,
      auto_wrap: false,
    } );
    timer_bg.addChild( timer_text, true );

    this.timer_bg = timer_bg;
    this.timer_text = timer_text;
  }

  private refreshTimer(): void
  {
    const remaining_ms = getChainOfferRemainingMs();

    if ( remaining_ms === null )
    {
      this.reconcileWithServer();
      if ( this.content && this.timer_bg )
      {
        this.content.removeChild( this.timer_bg );
        this.content.forceMakeShouldBeRendered();
        this.timer_bg = null;
        this.timer_text = null;
      }
      return;
    }

    this.buildTimer();

    const text = formatChainOfferRemaining( remaining_ms );
    if ( text === this.last_timer_text ) return;

    this.last_timer_text = text;
    this.timer_text?.setText( text );
  }

  private createContentTitle( content: UIElementHolder ): void
  {
    const title_y_design = 113;
    const title_h_design = 136;
    const title_font_size_design = 100;

    const title_gradient: UITextGradient = {
      direction: "vertical",
      stops: [
        { offset: 0, color: "#FEF3BB" },
        { offset: 1, color: "#DFB403" },
      ],
    };

    const title_layers: TitleLayerConfig[] = [
      {
        outline_width_design: 48,
        outline_offset_y_design: 11,
        outline_gradient_color: {
          direction: "vertical",
          stops: [
            { offset: 0, color: "#6C1F03" },
            { offset: 1, color: "#6C1F03" },
          ],
        },
      },
      {
        outline_width_design: 42,
        outline_offset_y_design: 7,
        outline_gradient_color: {
          direction: "vertical",
          stops: [
            { offset: 0, color: "#F8AE26" },
            { offset: 1, color: "#F8AE26" },
          ],
        },
      },
      {
        outline_width_design: 29,
        outline_offset_y_design: 7,
        outline_gradient_color: {
          direction: "vertical",
          stops: [
            { offset: 0, color: "#042385" },
            { offset: 1, color: "#032080" },
          ],
        },
      },
      {
        outline_width_design: 25,
        outline_offset_y_design: 3,
        outline_color: "#0062FB",
        shadow_blur_design: 35,
        shadow_color: "rgba(100, 27, 0, 0.89)",
        shadow_offset_y_design: 5,
      },
    ];

    const title_y = LayoutScaler.yFromDesign( title_y_design );
    const title_h = LayoutScaler.heightFromDesign( title_h_design );

    const title_w_ratio = 0.8;
    const screen_w = this.rect.getW();
    const title_w = screen_w * title_w_ratio;
    const title_x = ( screen_w - title_w ) / 2 - this.content_x;

    for ( const layer of title_layers )
    {
      const title = new UITextElement( this.ctx, title_x, title_y, title_w, title_h, {
        localization_key: "CHAIN_OFFER.TITLE",
        font_size: LayoutScaler.textSizeFromDesign( title_font_size_design ),
        font_weight: 900,
        text_style: TextStyleScheme.geologica,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        auto_wrap: false,
        scale_to_width: true,
        text_color: "#DFB403",
        gradient_text_color: title_gradient,
        outline_width: LayoutScaler.textBorderSizeFromDesign( layer.outline_width_design ),
        outline_color: layer.outline_color,
        outline_gradient_color: layer.outline_gradient_color,
        outline_offset_y: LayoutScaler.yFromDesign( layer.outline_offset_y_design ),
        shadow_blur: layer.shadow_blur_design !== undefined ? LayoutScaler.textBorderSizeFromDesign( layer.shadow_blur_design ) : undefined,
        shadow_color: layer.shadow_color,
        shadow_offset_y: layer.shadow_offset_y_design !== undefined ? LayoutScaler.yFromDesign( layer.shadow_offset_y_design ) : undefined,
      } );
      content.addChild( title );
    }
  }

  private initializeEventsController( canvas: HTMLCanvasElement ): void
  {
    if ( this.events_controller )
      this.events_controller.cleanup();

    this.events_controller = new BasicEventsController( canvas );
    this.events_controller.init(
      ( p: Vector2 ) => { super.onTouchStart( p, performance.now() ); },
      ( p: Vector2 ) => { super.onTouchEnd( p, performance.now() ); },
      ( p: Vector2 ) => { super.onTouchMove( p, performance.now() ); },
      true
    );
  }

  public destroy(): void
  {
    this.exiting = true;
    window.removeEventListener( 'offersEntitlementsChanged', this.handleEntitlementsChanged );

    if ( this.events_controller )
    {
      this.events_controller.cleanup();
      this.events_controller = null;
    }
  }

  private getBackgroundGradient(): CanvasGradient
  {
    if ( !this.background_gradient )
    {
      const gradient = this.ctx.createLinearGradient( 0, 0, 0, Game.screenH() );
      gradient.addColorStop( 0, "#003CCC" );
      gradient.addColorStop( 0.43, "#04A1FC" );
      gradient.addColorStop( 1, "#38C1FD" );
      this.background_gradient = gradient;
    }

    return this.background_gradient;
  }

  // Full-canvas fill: the safe-area strips would otherwise stay transparent
  // and expose the canvases below.
  public render(): void
  {
    if ( !this.shouldBeRendered() )
      return;

    if ( this.dom_mode )
    {
      this.setDomBackdrop( "linear-gradient(#003CCC 0%, #04A1FC 43%, #38C1FD 100%)" );
      this.startRender();
      this.finishRender();
      SafeAreas.syncBothSafeAreaOverlaysDom( this.getDomHost() );
      return;
    }

    this.ctx.fillStyle = this.getBackgroundGradient();
    this.ctx.fillRect( 0, 0, Game.screenW(), Game.screenH() );

    this.startRender();
    this.finishRender();
    SafeAreas.renderBothSafeAreaOverlays( this.ctx );
  }

  public update( now: number ): void
  {
    if ( now - this.timer_refreshed_at > 1000 )
    {
      this.timer_refreshed_at = now;
      this.refreshTimer();
    }
    this.updateBasic( now );
  }

  public name(): string
  {
    return "ChainOfferScreen";
  }
}
